import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from '@/components/ui/toast'
import { ConfirmDeleteDialog } from '@/components/shared'
import { useDeleteTask } from '@/hooks/useTasks'
import { getApiErrorMessage } from '@/services/api'
import type { Task } from '@/types'

interface Props {
  task: Task | null
  onClose: () => void
  onDeleted?: () => void
  // When true, navigate to the task list after a successful delete (detail flow).
  navigateOnDelete?: boolean
}

// 2.9.6 — Delete task confirmation — Figma 1336:31123.
export function DeleteTaskDialog({
  task,
  onClose,
  onDeleted,
  navigateOnDelete,
}: Props) {
  const { t } = useTranslation('tasks')
  const navigate = useNavigate()
  const del = useDeleteTask()

  const confirm = async () => {
    if (!task) return
    try {
      await del.mutateAsync(task.id)
      toast({ variant: 'success', title: t('deleteDialog.deleted') })
      onDeleted?.()
      if (navigateOnDelete) navigate('/tasks')
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('deleteDialog.deleteError')),
      })
    }
  }

  return (
    <ConfirmDeleteDialog
      open={!!task}
      onOpenChange={o => !o && onClose()}
      title={t('deleteDialog.title')}
      description={t('deleteDialog.confirmText')}
      confirmLabel={t('deleteDialog.confirm')}
      onConfirm={confirm}
      busy={del.isPending}
    />
  )
}
